import { client, useInvokeClient } from "@/ui/client.ts";
import { KnownErrorMessage } from "@/ui/db-error/index.tsx";
import { DialogHeader } from "@/ui/form-dialog/index.tsx";
import { FormSubmitError } from "@/ui/form-submit-error/index.tsx";
import { toKnownFailureMessage } from "@/ui/hooks/use-form.tsx";
import { FormTextField } from "@/ui/inputs/index.tsx";
import { LoadingIndicatorV2 } from "@/ui/loading-indicator/index.tsx";
import { ViewContainer } from "@/ui/max-width-container/index.tsx";
import { MobileNavLayout } from "@/ui/mobile-nav-layout/index.tsx";
import { FathomEvent } from "@/ui/paragraphs/fathom.tsx";
import { buttonV2 } from "@/ui/utils.css.ts";
import { DialogDisclosure, Form, Heading, HeadingLevel, useStoreState } from "@ariakit/react";
import {
  DialogTrigger,
  DownloadLink,
  FormSubmitButton,
  LetterheadFooter,
  ListItemContainer,
  ListItemContent,
  ListItemHeading,
  ListItemSummary,
  ListView,
  LoadingIndicator,
  ModalDialog,
  SyncIcon,
  trackEvent,
  useCurrentUser,
  useForm,
  validEmail,
} from "@indietabletop/appkit";
import type { OwnedProduct } from "@indietabletop/types";
import { useParams } from "wouter";
import { SectionContainer, SectionHeading, SectionParagraph } from "./Section/Section.tsx";
import { dtrpgSection, overrides, page, socials } from "./style.css.ts";

type SocialLink = {
  href: string;
  action: string;
  description: string;
  imgSrc: string;
  eventName: FathomEvent;
};

function SocialLink(props: {
  href: string;
  action: string;
  description: string;
  logoSrc: string;
  event: FathomEvent;
}) {
  return (
    <div className={socials.item}>
      <a
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => void trackEvent(props.event)}
        style={{ display: "contents" }}
      >
        <img src={props.logoSrc} alt="" className={socials.icon} />
      </a>

      <div className={socials.info}>{props.description}</div>

      <a
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => void trackEvent(props.event)}
        className={socials.action}
      >
        {props.action}
      </a>
    </div>
  );
}

function Socials(props: { links: SocialLink[] }) {
  return (
    <div className={socials.container}>
      {props.links.map((link) => {
        return (
          <SocialLink
            key={link.href}
            href={link.href}
            logoSrc={link.imgSrc}
            description={link.description}
            action={link.action}
            event={link.eventName}
          />
        );
      })}
    </div>
  );
}

function ShareToDtrpgDialog(props: { onSuccess: () => void }) {
  const sessionId = useSessionId();
  const currentUser = useCurrentUser();

  const { form, submitName } = useForm({
    defaultValues: { accountEmail: currentUser?.email ?? "" },
    async onSubmit({ values }) {
      const result = await client.shareToDtrpg(sessionId, values.accountEmail);
      return result.mapFailure((failure) => {
        if (failure.type === "API_ERROR") {
          switch (failure.code) {
            case 400: {
              return "Not shared. No eligible products found.";
            }

            case 502: {
              return "Failed to share due an issue on the side of DriveThruRPG. Please try again later.";
            }
          }
        }

        return toKnownFailureMessage(failure);
      });
    },
    onSuccess: props.onSuccess,
    validate: {
      accountEmail: validEmail,
    },
  });

  const dtrpgEmail = useStoreState(form, (store) => store.values.accountEmail);
  const accountsMatch = dtrpgEmail === currentUser?.email;

  return (
    <ModalDialog size="large">
      <Form store={form} style={{ padding: "2rem" }}>
        <DialogHeader heading="Share to DriveThruRPG" />
        <div style={{ marginBlockEnd: "1rem" }}>
          <SectionParagraph>
            All eligible products in your purchase will be shared to your DriveThruRPG library. You
            can only share your purchase once.
          </SectionParagraph>
        </div>

        <FormTextField
          name={form.names.accountEmail}
          label="Account Email"
          placeholder={currentUser?.email}
          hint="Your DriveThruRPG account email."
          required
        />

        {!accountsMatch && (
          <div style={{ marginBlockStart: "1rem" }}>
            ⚠️ You have chosen a DriveThruRPG account email that doesn&apos;t match your ITC account
            email.{" "}
            <strong>Make sure it is correct — a share cannot be changed once submitted.</strong>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            marginBlockStart: "2rem",
          }}
        >
          <FormSubmitError name={submitName} />
          <FormSubmitButton className={buttonV2({ theme: "black" })} loading={<LoadingIndicator />}>
            Share
          </FormSubmitButton>
        </div>
      </Form>
    </ModalDialog>
  );
}

import dtrpgSrc from "./dtrpg.png";

function DtrpgSection(props: { allProductsDelivered: boolean; revalidateView: () => void }) {
  const { allProductsDelivered } = props;

  return (
    <div>
      <SectionHeading>
        <img
          src={dtrpgSrc}
          alt="DriveThruRPG"
          style={{ display: "inline-block", maxInlineSize: "8em" }}
        />
      </SectionHeading>

      <SectionParagraph>
        DriveThruRPG user? Automatically share all eligible products to your library.
      </SectionParagraph>

      {allProductsDelivered ?
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            gap: "0.5rem",
            marginBlockStart: "0.5rem",
            fontStyle: "italic",
          }}
        >
          <SyncIcon status="IDLE" /> All eligible products shared!
        </div>
      : <DialogTrigger>
          <ShareToDtrpgDialog onSuccess={props.revalidateView} />
          <DialogDisclosure className={dtrpgSection.action}>Share</DialogDisclosure>
        </DialogTrigger>
      }
    </div>
  );
}

function CheckoutSessionView(props: {
  revalidateView: () => void;
  session: {
    products: OwnedProduct[];
    orderRef: string;
    dtrpg?: { allProductsDelivered: boolean };
  };
}) {
  const { session } = props;
  const { products, orderRef, dtrpg } = session;

  // Re-run the check access call to make sure that if new unlocks were
  // added, they show up in the app. This is important in case Stripe was too
  // slow notifying us that a purchase happened via webhooks and it was the
  // session check call that resulted in the purchased being processed.
  // useGetUnlocks({ gameCode: "greathelm" });

  // const unlocks = new Set(
  //   products
  //     .flatMap((product) => product.unlocks?.map((u) => u.feature) ?? [])
  //     .filter((feature) => FeatureUnlock.has(feature))
  //     .map((u) => FeatureUnlock.get(u)),
  // );

  return (
    <SectionContainer>
      <CheckoutHeader orderRef={orderRef} />

      <HeadingLevel>
        <div className={page.sections}>
          <DownloadsSection products={products} />

          {dtrpg && (
            <HeadingLevel>
              <DtrpgSection {...props} {...dtrpg} />
            </HeadingLevel>
          )}
          <LetterheadFooter />
        </div>
      </HeadingLevel>
    </SectionContainer>
  );
}

function CheckoutHeader(props: { orderRef: string }) {
  const { orderRef } = props;

  return (
    <div className={page.header}>
      <div className={page.overline}>Payment received! Thanks!</div>
      <Heading className={page.heading}>What next?</Heading>
      <div className={page.orderInfo}>
        Your payment has been processed successfully.
        <br />
        Order reference: <span className={page.orderRef}>{orderRef}</span>
      </div>
    </div>
  );
}

function DownloadsSection(props: { products: OwnedProduct[] }) {
  const { products } = props;

  const someMissingDownloads = products.some((p) => !p.downloadUrl);

  return (
    <div>
      <SectionHeading>Download your stuff</SectionHeading>

      <SectionParagraph>
        You are all set to download your new stuff! Your purchases are saved to your account, so you
        can always get back to them later from the app.
      </SectionParagraph>

      {someMissingDownloads && (
        <SectionParagraph>
          <strong>⚠️ Warning</strong>: Some digital products below are missing download links. This
          is probably an issue with our service. Our team has been automatically notified, but if
          you don&apos;t hear from us soon, please get in touch with support@indietabletop.club.
          Sorry about this!
        </SectionParagraph>
      )}

      <ListView className={overrides.downloadsList}>
        {products.map((product) => {
          return (
            <ListItemContainer key={product.id} className={overrides.downloadsContainer}>
              <ListItemContent>
                <ListItemHeading className={overrides.downloadHeading}>
                  {product.name}
                </ListItemHeading>

                <ListItemSummary className={overrides.downloadSummary}>
                  {product.downloadFileVersion && (
                    <span>Version {product.downloadFileVersion}</span>
                  )}
                  <span>{product.downloadFileSize}</span>
                </ListItemSummary>
              </ListItemContent>

              {product.downloadUrl && (
                <DownloadLink className={overrides.downloadButton} href={product.downloadUrl}>
                  Download
                </DownloadLink>
              )}
            </ListItemContainer>
          );
        })}
      </ListView>
    </div>
  );
}

function useGetCheckoutSession(sessionId: string) {
  const [result, { mutate }] = useInvokeClient("getCheckoutSessionV2", [sessionId]);

  return {
    result: result.mapSuccess((value) => ({
      // Set these first to allow overrides once these come from the endpoint
      links: [],

      ...value,
    })),

    revalidateView: () => mutate(),
  };
}

function useSessionId() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return sessionId;
}

export function CheckoutSuccessPage() {
  const sessionId = useSessionId();
  const { result, revalidateView } = useGetCheckoutSession(sessionId);

  if (result.isPending) {
    return (
      <MobileNavLayout title="">
        <ViewContainer padding="small">
          <LoadingIndicatorV2 />
        </ViewContainer>
      </MobileNavLayout>
    );
  }

  if (result.isFailure) {
    return (
      <MobileNavLayout title="">
        <ViewContainer padding="small">
          <KnownErrorMessage
            error={result.failure}
            // messages={{
            //   409: {
            //     title: "Payment not yet processed",
            //     description: `Our payments provider has not yet confirmed that a payment was received. Please check back later, or contact support if this issue persists.`,
            //   },
            // }}
          />
        </ViewContainer>
      </MobileNavLayout>
    );
  }

  return (
    <MobileNavLayout title="Success">
      <ViewContainer padding="small">
        <CheckoutSessionView session={result.value} revalidateView={revalidateView} />
      </ViewContainer>
    </MobileNavLayout>
  );
}
